/**
 * Anzeige-Helfer der Aufgaben & Erinnerungen. Alle Tage und Uhrzeiten in Berlin, als
 * 'JJJJ-MM-TT' / 'HH:mm' — so geht die Fälligkeit auch an den Server (kein Umweg über UTC).
 */
import type { Todo, TodoDue, TodoDueInput } from '../../lib/api';

const TZ = 'Europe/Berlin';

export function berlinYmd(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
}

export function berlinHm(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
}

export function addDaysYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Wochentag (0 = Montag) eines Tagesdatums. */
function weekdayOf(ymd: string): number {
  return (new Date(`${ymd}T12:00:00Z`).getUTCDay() + 6) % 7;
}

export function nextMondayYmd(from = berlinYmd()): string {
  const wd = weekdayOf(from);
  return addDaysYmd(from, 7 - wd);
}

/** Heute/Morgen oder „Do., 25.09." (+ Jahr, wenn nicht dieses Jahr). */
export function formatDayLabel(ymd: string): string {
  const today = berlinYmd();
  if (ymd === today) return 'Heute';
  if (ymd === addDaysYmd(today, 1)) return 'Morgen';
  if (ymd === addDaysYmd(today, -1)) return 'Gestern';
  const d = new Date(`${ymd}T12:00:00Z`);
  const sameYear = ymd.slice(0, 4) === today.slice(0, 4);
  return d.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
}

export function formatDue(due: TodoDue | null): string {
  if (!due) return 'Ohne Fälligkeit';
  return due.time ? `${formatDayLabel(due.date)}, ${due.time}` : formatDayLabel(due.date);
}

export function dueColor(todo: Pick<Todo, 'overdue' | 'due' | 'completedAt'>): string {
  if (todo.completedAt) return 'gray';
  if (todo.overdue) return 'red';
  if (todo.due?.date === berlinYmd()) return 'orange';
  return 'gray';
}

/** „Heute, 14:00" / „Do., 25.09., 14:00" */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${formatDayLabel(berlinYmd(d))}, ${berlinHm(d)}`;
}

/** „vor 5 Min.", „vor 3 Std.", „25.09., 14:10" */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff >= 0 && diff < 60 * 60_000) return `vor ${Math.max(1, Math.round(diff / 60_000))} Min.`;
  if (diff >= 0 && diff < 12 * 60 * 60_000) return `vor ${Math.round(diff / 3_600_000)} Std.`;
  return `${formatDayLabel(berlinYmd(d))}, ${berlinHm(d)}`;
}

export const PRIORITY_MARK = ['', '!', '!!', '!!!'];

export const PRIORITY_OPTIONS = [
  { value: '0', label: 'Keine' },
  { value: '1', label: '! niedrig' },
  { value: '2', label: '!! mittel' },
  { value: '3', label: '!!! hoch' },
];

export interface DuePreset {
  key: string;
  label: string;
  get: () => TodoDueInput;
}

/** Auf die nächsten 5 Minuten aufgerundet, in einer Stunde. */
function inOneHour(): TodoDueInput {
  const t = new Date(Date.now() + 60 * 60_000);
  t.setMinutes(Math.ceil(t.getMinutes() / 5) * 5, 0, 0);
  return { date: berlinYmd(t), time: berlinHm(t) };
}

/** Schnellauswahl fürs Anlegen — ganzer Tag, wie in Apple Erinnerungen. */
export const DUE_PRESETS: DuePreset[] = [
  { key: 'today', label: 'Heute', get: () => ({ date: berlinYmd(), time: null }) },
  { key: 'tomorrow', label: 'Morgen', get: () => ({ date: addDaysYmd(berlinYmd(), 1), time: null }) },
  { key: 'monday', label: 'Nächster Montag', get: () => ({ date: nextMondayYmd(), time: null }) },
  { key: 'week', label: 'In 1 Woche', get: () => ({ date: addDaysYmd(berlinYmd(), 7), time: null }) },
];

/** Schnellauswahl fürs Verschieben — die Uhrzeit bleibt, wenn es eine gab. */
export function snoozePresets(current: TodoDue | null): DuePreset[] {
  const time = current?.time ?? null;
  return [
    { key: 'hour', label: 'In 1 Std.', get: inOneHour },
    { key: 'tomorrow', label: 'Morgen', get: () => ({ date: addDaysYmd(berlinYmd(), 1), time }) },
    { key: 'monday', label: 'Nächster Montag', get: () => ({ date: nextMondayYmd(), time }) },
    { key: 'week', label: 'In 1 Woche', get: () => ({ date: addDaysYmd(berlinYmd(), 7), time }) },
  ];
}

/** Ticket-Verknüpfung → Sprungziel im Portal */
export function ticketHref(link: { kind: string; refId: string; refNumber: string | null }): string {
  if (link.kind === 'WECLAPP_TICKET') return `/t/${encodeURIComponent(link.refNumber ?? link.refId)}`;
  return `/tickets?open=${encodeURIComponent(link.refId)}`;
}

export function customerHref(customerId: string): string {
  return `/customer-hub?customerId=${encodeURIComponent(customerId)}`;
}

export function ticketLinkOf(todo: Pick<Todo, 'links'>) {
  return todo.links.find((l) => l.kind === 'WECLAPP_TICKET' || l.kind === 'CW_TICKET') ?? null;
}

export function customerLinkOf(todo: Pick<Todo, 'links'>) {
  return todo.links.find((l) => l.kind === 'CUSTOMER') ?? null;
}

/** Kurzform für die Liste, Langform für Tooltip und Detail */
export const RESOLVE_WHEN_SHORT: Record<string, string> = {
  CUSTOMER_REPLIED: 'falls keine Antwort',
  TICKET_CLOSED: 'bis Ticket zu',
  CALLBACK_DONE: 'bis Rückruf erledigt',
  CUSTOMER_REACHED: 'bis erreicht',
  REQUEST_ORDERED: 'bis bestellt',
};

export const RESOLVE_WHEN_LONG: Record<string, string> = {
  CUSTOMER_REPLIED: 'Erledigt sich von selbst, wenn der Kunde am Ticket antwortet (Mail oder Kundenportal) oder das Ticket geschlossen wird',
  TICKET_CLOSED: 'Erledigt sich von selbst, wenn das Ticket geschlossen wird',
  CALLBACK_DONE: 'Erledigt sich von selbst, wenn der Rückruf in der Rückrufliste erledigt ist',
  CUSTOMER_REACHED: 'Erledigt sich von selbst, sobald ihr die Person erreicht (Anruf „erreicht" dokumentiert oder Gespräch laut Telefonanlage)',
  REQUEST_ORDERED: 'Erledigt sich von selbst, sobald ein Admin die Materialanfrage als bestellt markiert. Bis dahin jeden Bürotag eine Erinnerung.',
};

/** „bis Fr., 10.10." */
export function untilLabel(ymd: string): string {
  return `bis ${new Date(`${ymd}T12:00:00Z`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })}`;
}

/** Zwei Werktage weiter (Sa/So übersprungen) — Standard fürs Nachfassen. */
export function workdaysAheadYmd(days: number, from = berlinYmd()): string {
  let d = from;
  let left = days;
  while (left > 0) {
    d = addDaysYmd(d, 1);
    const wd = new Date(`${d}T12:00:00Z`).getUTCDay();
    if (wd !== 0 && wd !== 6) left--;
  }
  return d;
}

export const TODO_QUERY_KEYS = {
  all: ['todos'] as const,
  /**
   * Was eine Aufgabe am Ticket ändert, steht dort auch als interner Kommentar
   * (backend services/todos/todoTicketNotes.ts). Das Cockpit lädt deshalb mit neu —
   * sonst fehlte der Vermerk im Verlauf bis zum nächsten Abruf.
   */
  ticketCockpits: ['weclapp-cockpit'] as const,
};
